/* eslint-disable import/no-cycle */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { position } from 'styled-system';
import { Box } from 'rebass';
import { useLocation } from 'react-router-dom';

import { ScrollShadow } from '@lib/scroll-shadow';
import { Hamburger } from '@oca/icons';
import { NavList } from './nav-list';
import { RecordFlowButton } from './recordflow-button';

export const Navigation = React.memo(function Navigation(props) {
  const { routes, open, toggle, onClose } = props;
  const location = useLocation();
  const toggleLabel = open ? 'Collapse sidebar' : 'Expand sidebar';

  return (
    <NavigationWrapper data-is-open={open}>
      <Inner>
        <HeaderArea>
          <LogoLink href="/" title="OCA Tawfiq">
            <FullLogo
              src="/logo/logo-text.svg"
              alt="OCA Tawfiq"
              data-visible={open}
            />
            <MarkLogo src="/logo/logo.svg" alt="" data-visible={!open} />
          </LogoLink>
        </HeaderArea>

        <NavContent>
          <ScrollShadow
            topShadowColors={{
              active:
                'linear-gradient(180deg, rgba(15, 23, 42, 0.05) 0%, rgba(255, 255, 255, 0) 100%)',
              inactive: '#FFFFFF',
            }}
            bottomShadowColors={{
              active:
                'linear-gradient(0deg, rgba(15, 23, 42, 0.05) 0%, rgba(255, 255, 255, 0) 100%)',
              inactive: '#FFFFFF',
            }}
            shadowSize={12}
            hideScrollbar
          >
            <NavList
              collapsed={!open}
              depth={0}
              location={location}
              pages={routes}
              onItemClick={onClose}
            />
          </ScrollShadow>
        </NavContent>

        <FooterArea>
          <RecordFlowButton collapsed={!open} />
        </FooterArea>
      </Inner>

      <ToggleButton
        type="button"
        onClick={toggle}
        data-open={open}
        title={toggleLabel}
        aria-label={toggleLabel}
      >
        <Hamburger />
      </ToggleButton>
    </NavigationWrapper>
  );
});

Navigation.propTypes = {
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string,
      name: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      routes: PropTypes.array,
    }),
  ).isRequired,
  // eslint-disable-next-line react/require-default-props
  open: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  toggle: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  onClose: PropTypes.func,
};

const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

const NavigationWrapper = styled(Box)`
  position: relative;
  height: 100%;
  background: #ffffff;
  border-right: 1px solid #eaecf0;
  transition: width 260ms ${EASE};
  will-change: width;
  user-select: none;

  &[data-is-open='true'] {
    width: var(--nav-expanded-width);
  }
  &[data-is-open='false'] {
    width: var(--nav-collapsed-width);
  }
  ${position}
`;

/* Clips content while the width animates; the toggle lives outside of it so
   it can straddle the sidebar edge. */
const Inner = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

/* Fixed height in both states so nothing shifts vertically on toggle. */
const HeaderArea = styled.div`
  position: relative;
  height: 92px;
  flex-shrink: 0;
  border-bottom: 1px solid #f1f5f9;
`;

const LogoLink = styled.a`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: block;
  text-decoration: none;
`;

/* Both logos stay mounted at fixed positions and simply crossfade. */
const logoBase = `
  position: absolute;
  top: 50%;
  object-fit: contain;
  transform: translateY(-50%);
  transition: opacity 200ms ease;
  &[data-visible='false'] {
    opacity: 0;
    pointer-events: none;
  }
  &[data-visible='true'] {
    opacity: 1;
    transition-delay: 70ms;
  }
`;

const FullLogo = styled.img`
  ${logoBase}
  left: 20px;
  height: 60px;
  max-width: 200px;
`;

/* (80px rail - 52px mark) / 2 = 14px keeps the mark centred when collapsed. */
const MarkLogo = styled.img`
  ${logoBase}
  left: 14px;
  width: 52px;
  height: 52px;
`;

const ToggleButton = styled.button`
  position: absolute;
  /* (92px header - 26px button) / 2 keeps it centred on the logo row. */
  top: 33px;
  right: -13px;
  z-index: 2;
  width: 26px;
  height: 26px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 50%;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.08);
  cursor: pointer;
  outline: none;
  transition: background-color 150ms ease, border-color 150ms ease,
    box-shadow 150ms ease;

  svg {
    width: 16px;
    height: 16px;
    transition: transform 260ms ${EASE};
  }

  &[data-open='false'] svg {
    transform: scaleX(-1);
  }

  &:hover,
  &:focus-visible {
    border-color: #7b44f4;
    box-shadow: 0 2px 8px rgba(123, 68, 244, 0.2);
  }

  &:hover svg path {
    stroke: #7b44f4;
  }
`;

const NavContent = styled(Box)`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

/* Horizontal padding matches the nav items (18px) so the card and the items
   keep the same left/right edges while the rail is collapsing/expanding. */
const FooterArea = styled.div`
  flex-shrink: 0;
  padding: 12px 18px;
  background: #ffffff;
  border-top: 1px solid #f1f5f9;
`;
